<template>
  <div
    :id="elementId"
    ref="containerRef"
    :class="{ reduced, fullwidth: fullWidth }"
  >
    <FederatedSearchLoadingPlaceholder
      v-if="!isInitialized"
      :class="{ fullwidth: fullWidth }"
      :with-test-ids="true"
      @focusin="initAutocomplete"
      @pointerdown="initAutocomplete"
    />
  </div>
</template>

<script setup lang="ts">
import { nextTick, onUnmounted, ref, watch, render as vueRender } from 'vue'
import { useI18n } from 'vue-i18n'
import { onClickOutside } from '@vueuse/core'
import FederatedSearchLoadingPlaceholder from './FederatedSearchLoadingPlaceholder.vue'
import { useAlgoliaSearchClient } from '~/composables/algolia/useAlgoliaSearchClient'
import { useAlgoliaSearchInsights } from '~/composables/algolia/useAlgoliaSearchInsights'
import { useLastViewedEvents } from '~/composables/useLastViewedEvents'
import { sanitizeAlgoliaQueryId } from '~/utils/helpers'
import { getProductPath, mountUrlParamsFromItem } from './helpers'
import { useFederatedSearchAnalyticsSession } from '~/composables/algolia/useFederatedSearchAnalyticsSession'
import { useFederatedSearchSources } from './useFederatedSearchSources'
import { createPanelLayout } from './renderPanel'
import {
  useRouter,
  useRoute,
  useRuntimeConfig,
  useLocalePath,
  navigateTo,
} from '#imports'

const {
  elementId,
  reduced = false,
  fullWidth = false,
  analyticsPlacement,
} = defineProps<{
  elementId: string
  reduced?: boolean
  fullWidth?: boolean
  analyticsPlacement?: 'homepage' | 'navbar'
}>()

const { t, locale } = useI18n()
const { isMobile } = useDevice()
const router = useRouter()
const localePath = useLocalePath()
const config = useRuntimeConfig()
// Reuse the shared `$algolia` client instead of building a second one — see KFT-3678.
const searchClient = useAlgoliaSearchClient()
const { client: algoliaInsightsClient, token: userToken } =
  useAlgoliaSearchInsights()
const algoliaAutocompleteInstance = ref<any>(null)
const isInitialized = ref(false)
const { lastViewedEvents, loadLastViewedEvents } = useLastViewedEvents(false)
const hasLoadedLastViewedEvents = ref(false)
const eventsQueryId = ref<string | null>(null)
const isAutocompleteOpen = ref(false)
const containerRef = ref<HTMLElement | null>(null)
const { setFederatedSearchAnalyticsOutcome } =
  useFederatedSearchAnalyticsSession()

const getSources = useFederatedSearchSources({
  searchClient,
  locale,
  isMobile,
  localePath,
  t,
  router,
  eventsQueryId,
  appEnv: config.public.appEnv,
  analyticsPlacement,
})

let isNavigatingWithKeyboard = false
let removeKeyboardListeners: (() => void) | null = null

// Lazy-init: @algolia/autocomplete-js is dynamic-imported and the autocomplete
// instance is only created on first interaction with the placeholder input, so
// the bundle and its document listeners stay off pages until the box is used.
const initAutocomplete = async () => {
  if (isInitialized.value) return
  isInitialized.value = true

  const { autocomplete } = await import('@algolia/autocomplete-js')

  // Wait for the placeholder to be removed so autocomplete mounts into an empty
  // container.
  await nextTick()

  const el = document.getElementById(elementId)
  if (!el) return

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      isNavigatingWithKeyboard = true
    }
  }

  const handleMouseDown = () => {
    isNavigatingWithKeyboard = false
  }

  document.addEventListener('keydown', handleKeyDown)
  document.addEventListener('mousedown', handleMouseDown)
  removeKeyboardListeners = () => {
    document.removeEventListener('keydown', handleKeyDown)
    document.removeEventListener('mousedown', handleMouseDown)
  }

  algoliaAutocompleteInstance.value = autocomplete({
    // Deterministic, instance-unique element ids (e.g. `#banner-autocomplete-input`)
    // instead of a shared `autocomplete-N` counter that shifts with init order.
    id: elementId,
    container: `#${elementId}`,
    defaultActiveItemId: null,
    onStateChange({ state }) {
      isAutocompleteOpen.value = state.isOpen

      if (state.isOpen && !hasLoadedLastViewedEvents.value) {
        hasLoadedLastViewedEvents.value = true
        loadLastViewedEvents()
      }

      if (
        isNavigatingWithKeyboard &&
        state.activeItemId === null &&
        state.collections.some((col) => col.items.length > 0)
      ) {
        algoliaAutocompleteInstance.value.setActiveItemId(0)
      }
    },
    openOnFocus: true,
    insights: {
      insightsClient: algoliaInsightsClient,
      onItemsChange({ insights, insightsEvents }) {
        const events = insightsEvents.map((insightsEvent) => ({
          ...insightsEvent,
          userToken: userToken.value,
        }))
        insights.viewedObjectIDs(...events)
      },
    },
    translations: {
      detachedCancelButtonText: t('search.buttons.cancel'),
    },
    renderer: { createElement },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    render({ elements, render, state, setIsOpen }, root) {
      const panelLayout = createPanelLayout({
        elements,
        state,
        setIsOpen,
        lastViewedEvents,
        localePath,
        t,
      })
      vueRender(panelLayout, root)

      if (fullWidth) {
        const resultsPanel = document.getElementsByClassName('aa-Panel')
        if (resultsPanel && resultsPanel[0]) {
          resultsPanel[0].classList.add('aa-Panel-fullwidth')
        }
      }
    },
    getSources,
    placeholder: t('search.algoliaAutocomplete.placeholder'),
    onSubmit({ state, setIsOpen }: any) {
      setIsOpen(false)

      if (state?.context?.redirects?.url) {
        navigateTo(state?.context.redirects?.url, { external: true })
        return
      }

      setFederatedSearchAnalyticsOutcome('search_results')

      const updatedQuery = {
        ...(state?.query
          ? { searchquery: state?.query, resetFilters: 'true' }
          : {}),
      }

      navigateTo({
        path: localePath('/search/'),
        query: updatedQuery,
      })
    },
    navigator: {
      navigate({ state }) {
        const activeItem = state?.collections
          ?.flatMap((collection) => collection.items)
          ?.at(state?.activeItemId ?? -1)

        if (isNavigatingWithKeyboard && activeItem) {
          if (activeItem.__autocomplete_indexName.includes('categories')) {
            setFederatedSearchAnalyticsOutcome('category_results')
            navigateTo(localePath(`/c/${activeItem?.slug}`))
            return
          }

          if (activeItem.__autocomplete_indexName.includes('locations')) {
            setFederatedSearchAnalyticsOutcome('location_results')
            navigateTo(
              localePath(`/search/?${mountUrlParamsFromItem(activeItem)}`)
            )
            return
          }

          if (
            activeItem.__autocomplete_indexName.includes('events') ||
            activeItem.__autocomplete_indexName.includes('products')
          ) {
            const path = localePath(getProductPath(activeItem))
            const qid = sanitizeAlgoliaQueryId(eventsQueryId.value)
            window.location.href = qid ? `${path}?qid=${qid}` : path
            return
          }
        }

        if (state?.context?.redirects?.url) {
          navigateTo(state.context.redirects.url, { external: true })
          return
        }

        setFederatedSearchAnalyticsOutcome('search_results')
        navigateTo({
          path: localePath('/search/'),
          query: { searchquery: state?.query },
        })
      },
    },
  })

  // Forward the user's interaction to the freshly-mounted autocomplete: focus
  // the inline input, or open the detached overlay on touch/mobile where no
  // inline input exists.
  await nextTick()
  const inlineInput = el.querySelector<HTMLInputElement>('.aa-Input')
  if (inlineInput) {
    inlineInput.focus()
  } else {
    algoliaAutocompleteInstance.value?.setIsOpen?.(true)
    await nextTick() // let the detached overlay mount
  }

  // Carry the placeholder's e2e hook onto the mounted input so the selector
  // survives lazy-init, scoped to this instance via its deterministic id.
  document
    .getElementById(`${elementId}-input`)
    ?.setAttribute('data-test-id', 'federated-search-input')
}

onUnmounted(() => {
  removeKeyboardListeners?.()
  algoliaAutocompleteInstance.value?.destroy?.()
})

if (import.meta.client && fullWidth) {
  onClickOutside(containerRef, (event: Event) => {
    const t = event.target as HTMLElement | null
    if (t?.closest?.('.aa-DetachedOverlay, .aa-DetachedContainer')) {
      return
    }
    if (isAutocompleteOpen.value && algoliaAutocompleteInstance.value) {
      algoliaAutocompleteInstance.value.setIsOpen(false)
    }
  })
}

const route = useRoute()
watch(
  () => route.fullPath,
  () => {
    setTimeout(() => {
      algoliaAutocompleteInstance.value?.setIsOpen(false)
    }, 0)
  }
)
</script>
